/* eslint-disable max-lines */
import hash from 'hash-it';
import merge from 'lodash-es/merge.js';
import omit from 'lodash-es/omit.js';
import { TranslatableError } from '../../translation/translatable-error.js';
import { DimensionalElement } from '../base.js';
import { create } from '../factory.js';
import { isDimensionalBaseMeasure } from '../measures/measures.js';
import { DateLevels, MetadataTypes } from '../types.js';
import { getDefaultBaseFilterConfig, getDefaultMembersFilterConfig, } from './filter-config-utils.js';
/**
 * Different text operators that can be used with text filters
 *
 * @internal
 */
export const TextOperators = {
    Contains: 'contains',
    StartsWith: 'startsWith',
    EndsWith: 'endsWith',
    Equals: 'equals',
    DoesntEqual: 'doesntEqual',
    DoesntStartWith: 'doesntStartWith',
    DoesntContain: 'doesntContain',
    DoesntEndWith: 'doesntEndWith',
    Like: 'like',
};
/**
 * Different numeric operators that can be used with numeric filters
 */
export const NumericOperators = {
    Equals: 'equals',
    DoesntEqual: 'doesntEqual',
    From: 'from',
    FromNotEqual: 'fromNotEqual',
    To: 'to',
    ToNotEqual: 'toNotEqual',
};
/**
 * Different date operators that can be used with date filters
 *
 * @internal
 */
export const DateOperators = {
    From: 'from',
    To: 'to',
    Last: 'last',
    Next: 'next',
    Anchor: 'Anchor',
};
/**
 * Different logical operators that can be used with logical filters
 *
 * @internal
 */
export const LogicalOperators = {
    Union: 'or',
    Intersection: 'and',
    Exclude: 'exclude',
};
/**
 * Different ranking operators that can be used with ranking filter
 *
 * @internal
 */
export const RankingOperators = {
    Top: 'top',
    Bottom: 'bottom',
};
/**
 * Different filter types
 *
 * @internal
 */
export const FilterTypes = {
    logicalAttribute: 'logicalAttribute',
    members: 'members',
    exclude: 'exclude',
    measure: 'measure',
    ranking: 'ranking',
    text: 'text',
    numeric: 'numeric',
    dateRange: 'dateRange',
    relativeDate: 'relativeDate',
    cascading: 'cascading',
    advanced: 'advanced',
};
// CLASSES
/**
 * base implementation for filter classes
 *
 * @internal
 */
class AbstractFilter extends DimensionalElement {
    constructor(att, filterType, config, composeCode) {
        super('filter', MetadataTypes.Filter, undefined, undefined, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'AbstractFilter';
        this.filterType = filterType;
        // need to set isScope
        // to make filter complete for comparison
        this.isScope = true;
        AbstractFilter.checkAttributeSupport(att);
        this.attribute = att;
        this.config = merge({}, getDefaultBaseFilterConfig(), config !== null && config !== void 0 ? config : {});
    }
    get name() {
        // to hexadecimal string
        return hash(this.jaql()).toString(16);
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.filterType = this.filterType;
        result.attribute = this.attribute.serialize();
        if (this.config) {
            result.config = this.config;
        }
        result.composeCode = this.composeCode;
        return result;
    }
    /**
     * Gets the JAQL representation of this instance
     *
     * @param nested - defines whether the JAQL is nested within parent JAQL statement or a root JAQL element
     */
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        const result = this.attribute.jaql(false);
        const level = this.attribute;
        if (level.getFormat && level.getFormat() !== undefined) {
            const granularityJaql = level.translateGranularityToJaql();
            result.format = {
                mask: {
                    [granularityJaql.level]: level.getFormat(),
                },
            };
        }
        result.jaql.filter = this.filterJaql();
        // prioritize attribute dataSource for the use case of multi-source dashboard
        if (this.attribute.dataSource) {
            result.jaql.datasource = this.attribute.dataSource;
        }
        if (this.isScope) {
            result.panel = 'scope';
        }
        return nested === true ? result.jaql : result;
    }
    static checkAttributeSupport(attribute) {
        const { granularity } = attribute;
        if (granularity === DateLevels.Hours ||
            granularity === DateLevels.MinutesRoundTo30 ||
            granularity === DateLevels.MinutesRoundTo15 ||
            granularity === DateLevels.Minutes ||
            granularity === DateLevels.Seconds) {
            throw new TranslatableError('errors.filter.unsupportedDatetimeLevel');
        }
    }
    static disabledJaql(nested) {
        return nested ? { filter: {} } : { jaql: { filter: {} } };
    }
}
/**
 * @internal
 */
export class LogicalAttributeFilter extends AbstractFilter {
    constructor(filters, operator, config, composeCode) {
        super(filters[0].attribute, FilterTypes.logicalAttribute, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'LogicalAttributeFilter';
        this.operator = operator;
        this.filters = filters;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.operator}_${this.filters.map((f) => f.id).join()}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.operator = this.operator;
        result.filters = this.filters.map((f) => f.serialize());
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        result[this.operator] = this.filters.map((f) => f.filterJaql());
        return result;
    }
}
/**
 * @internal
 */
export class MembersFilter extends AbstractFilter {
    constructor(attribute, members, config, composeCode) {
        super(attribute, FilterTypes.members, undefined, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'MembersFilter';
        this.members = members !== null && members !== void 0 ? members : [];
        if (this.members.filter((m) => m === null || m === undefined).length > 0) {
            throw new TranslatableError('errors.filter.membersFilterNullMember', {
                attributeId: attribute.id,
            });
        }
        // merge default config and input config into a new object
        // to avoid mutation
        this.config = merge({}, getDefaultMembersFilterConfig(), config !== null && config !== void 0 ? config : {});
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.attribute.id}_${this.members.map((m) => m.toString()).join()}`;
    }
    get name() {
        // to hexadecimal string
        return hash([this.jaql(), omit(this.config, ['guid', 'originalFilterJaql'])]).toString(16);
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        if (this.config) {
            result.config = (this.config.backgroundFilter
                ? Object.assign(Object.assign({}, this.config), { backgroundFilter: this.config.backgroundFilter.serialize() }) : this.config);
        }
        result.members = this.members;
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const membersFilterJaql = {
            members: this.members.map((m) => m.toString()),
        };
        const filterJaql = this.config.excludeMembers
            ? { exclude: membersFilterJaql }
            : membersFilterJaql;
        if (this.config.backgroundFilter) {
            return {
                and: [filterJaql, this.config.backgroundFilter.filterJaql()],
            };
        }
        return filterJaql;
    }
}
/**
 * @internal
 */
export class CascadingFilter extends AbstractFilter {
    constructor(filters, config, composeCode) {
        super(filters[0].attribute, FilterTypes.cascading, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'CascadingFilter';
        this._filters = filters;
    }
    /**
     * Returns the level filters with the root config applied.
     */
    get filters() {
        const { disabled, locked } = this.config;
        return this._filters.map((filter) => {
            filter.config = Object.assign(Object.assign({}, filter.config), { disabled, locked });
            return filter;
        });
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.filterType}_${this._filters.map((f) => f.id).join()}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result._filters = this.filters.map((f) => f.serialize());
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        // return empty object as jaql is handled by jaql() method
        return {};
    }
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        // return jaql of all level filters treated as scope filters
        return this.filters.map((f) => {
            f.isScope = true;
            return f.jaql(nested);
        });
    }
}
/**
 * @internal
 */
export class ExcludeFilter extends AbstractFilter {
    constructor(filter, input, config, composeCode) {
        super(filter.attribute, FilterTypes.exclude, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'ExcludeFilter';
        this.input = input;
        this.filter = filter;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `exclude_${this.filter.id}`;
        if (this.input) {
            result += '_from_' + this.input.id;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.filter = this.filter.serialize();
        if (this.input) {
            result.input = this.input.serialize();
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        const exclusion = this.filter.filterJaql();
        if (this.input) {
            result.filter = this.input.filterJaql();
            result.filter.filter = { exclude: exclusion };
        }
        else {
            result.exclude = exclusion;
        }
        return result;
    }
}
/**
 * @internal
 */
export class DoubleOperatorFilter extends AbstractFilter {
    constructor(att, filterType, operatorA, valueA, operatorB, valueB, config, composeCode) {
        super(att, filterType, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'DoubleOperatorFilter';
        if (operatorA && valueA !== undefined) {
            this.valueA = valueA;
            this.operatorA = operatorA;
        }
        if (operatorB && valueB !== undefined) {
            this.operatorB = operatorB;
            this.valueB = valueB;
        }
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.attribute.id}`;
        if (this.operatorA && this.valueA !== undefined) {
            result += `_${this.operatorA}:${this.valueA}`;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result += `_${this.operatorB}:${this.valueB}`;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        if (this.operatorA) {
            result.operatorA = this.operatorA;
        }
        if (this.operatorB) {
            result.operatorB = this.operatorB;
        }
        if (this.valueA !== undefined) {
            result.valueA = this.valueA;
        }
        if (this.valueB !== undefined) {
            result.valueB = this.valueB;
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        if (this.operatorA && this.valueA !== undefined) {
            result[this.operatorA] = this.valueA;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result[this.operatorB] = this.valueB;
        }
        return result;
    }
}
/**
 * @internal
 */
export class MeasureFilter extends DoubleOperatorFilter {
    constructor(att, measure, operatorA, valueA, operatorB, valueB, config, composeCode) {
        super(att, FilterTypes.measure, operatorA, valueA, operatorB, valueB, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'MeasureFilter';
        this.measure = measure;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.attribute.id}_${this.measure.id}`;
        if (this.operatorA && this.valueA !== undefined) {
            result += `_${this.operatorA}:${this.valueA}`;
        }
        if (this.operatorB && this.valueB !== undefined) {
            result += `_${this.operatorB}${this.valueB}`;
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.measure = this.measure.serialize();
        return result;
    }
    jaql(nested) {
        if (this.config.disabled) {
            return AbstractFilter.disabledJaql(nested);
        }
        const result = super.jaql(nested);
        if (isDimensionalBaseMeasure(this.measure)) {
            Object.entries(this.measure.jaql().jaql).forEach(([key, value]) => {
                result.jaql[key] = value;
            });
        }
        return result;
    }
}
/**
 * @internal
 */
export class RankingFilter extends AbstractFilter {
    constructor(att, measure, operator, count, config, composeCode) {
        super(att, FilterTypes.ranking, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'RankingFilter';
        this.count = count;
        this.operator = operator;
        this.measure = measure;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `${this.operator}_${this.count}_${this.attribute.id}_by_${this.measure.id}`;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.count = this.count;
        result.operator = this.operator;
        result.measure = this.measure.serialize();
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        const result = {};
        result[this.operator] = this.count;
        result.by = this.measure.jaql(true);
        return result;
    }
}
/**
 * @internal
 */
export class NumericFilter extends DoubleOperatorFilter {
    constructor(att, operatorA, valueA, operatorB, valueB, config, composeCode) {
        super(att, FilterTypes.numeric, operatorA, valueA, operatorB, valueB, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'NumericFilter';
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        return super.serialize();
    }
}
/**
 * @internal
 */
export class TextFilter extends DoubleOperatorFilter {
    constructor(att, operator, value, config, composeCode) {
        super(att, FilterTypes.text, operator, value, undefined, undefined, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'TextFilter';
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        return super.serialize();
    }
}
/**
 * @internal
 */
export class DateRangeFilter extends DoubleOperatorFilter {
    constructor(levelAttribute, valueFrom, valueTo, config, composeCode) {
        super(levelAttribute, FilterTypes.dateRange, DateOperators.From, valueFrom, DateOperators.To, valueTo, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'DateRangeFilter';
        if (typeof valueFrom === 'object') {
            this.valueA = valueFrom.toISOString();
        }
        if (typeof valueTo === 'object') {
            this.valueB = valueTo.toISOString();
        }
    }
    get level() {
        return this.attribute;
    }
    get from() {
        return this.valueA;
    }
    get to() {
        return this.valueB;
    }
    /**
     * Gets JAQL representing this Filter instance
     */
    filterJaql() {
        return super.filterJaql();
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        return super.serialize();
    }
}
/**
 * @internal
 */
export class RelativeDateFilter extends AbstractFilter {
    constructor(levelAttribute, offset, count, operator, anchor, config, composeCode) {
        super(levelAttribute, FilterTypes.relativeDate, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'RelativeDateFilter';
        if (!operator) {
            operator = DateOperators.Next;
        }
        this.anchor = anchor;
        this.operator = operator;
        this.offset = offset;
        this.count = count;
    }
    get level() {
        return this.attribute;
    }
    /**
     * gets the element's ID
     */
    get id() {
        let result = `${this.level.id}_${this.operator}_${this.offset}_${this.count}`;
        if (this.anchor) {
            if (typeof this.anchor === 'string') {
                result += `_${this.anchor}`;
            }
            else if (typeof this.anchor === 'object') {
                result += `_${this.anchor.toISOString()}`;
            }
        }
        return result;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.offset = this.offset;
        result.count = this.count;
        result.operator = this.operator;
        if (this.anchor) {
            result.anchor = typeof this.anchor === 'string' ? this.anchor : this.anchor.toISOString();
        }
        return result;
    }
    /**
     * Gets JAQL representing this Filter instance
     *
     */
    filterJaql() {
        const result = {};
        result[this.operator] = {
            offset: this.offset,
            count: this.count,
        };
        if (this.anchor) {
            if (typeof this.anchor === 'string') {
                result[this.operator].anchor = this.anchor;
            }
            else if (typeof this.anchor === 'object') {
                result[this.operator].anchor = this.anchor.toISOString();
            }
        }
        return result;
    }
}
/**
 * @internal
 */
export class CustomFilter extends AbstractFilter {
    constructor(att, jaql, config, composeCode) {
        super(att, FilterTypes.advanced, config, composeCode);
        /**
         * @internal
         */
        this.__serializable = 'CustomFilter';
        // remove filterType from jaql as it is not needed
        delete jaql.filterType;
        this.jaqlExpression = jaql;
    }
    /**
     * gets the element's ID
     */
    get id() {
        return `custom_${this.attribute.id}_${this.config.guid}`;
    }
    /**
     * Gets JAQL representing this Filter instance
     *
     */
    filterJaql() {
        return this.jaqlExpression;
    }
    /**
     * Gets a serializable representation of the element
     */
    serialize() {
        const result = super.serialize();
        result.jaqlExpression = this.jaqlExpression;
        return result;
    }
}
/**
 * Checks if a filter is a CustomFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isCustomFilter(filter) {
    return filter && filter.__serializable === 'CustomFilter';
}
/**
 * Checks if a filter is a MembersFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isMembersFilter(filter) {
    return filter && filter.__serializable === 'MembersFilter';
}
/**
 * Checks if a filter is a NumericFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isNumericFilter(filter) {
    return filter && filter.__serializable === 'NumericFilter';
}
/**
 * Checks if a filter is a TextFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isRankingFilter(filter) {
    return filter && filter.__serializable === 'RankingFilter';
}
/**
 * Checks if a filter is a MeasureFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isMeasureFilter(filter) {
    return filter && filter.__serializable === 'MeasureFilter';
}
/**
 * Checks if a filter is a ExcludeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isExcludeFilter(filter) {
    return filter && filter.__serializable === 'ExcludeFilter';
}
/**
 * Checks if a filter is a LogicalAttributeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isLogicalAttributeFilter(filter) {
    return filter && filter.__serializable === 'LogicalAttributeFilter';
}
/**
 * Checks if a filter is a CascadingFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isCascadingFilter(filter) {
    return filter && filter.__serializable === 'CascadingFilter';
}
/**
 * Checks if a filter is a RelativeDateFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isRelativeDateFilter(filter) {
    return filter && filter.__serializable === 'RelativeDateFilter';
}
/**
 * Checks if a filter is a TextFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isTextFilter(filter) {
    return filter && filter.__serializable === 'TextFilter';
}
/**
 * Checks if a filter is a DateRangeFilter.
 *
 * @param filter - The filter to check.
 * @internal
 */
export function isDateRangeFilter(filter) {
    return filter && filter.__serializable === 'DateRangeFilter';
}
/**
 * @param json - Filter JSON representation
 * @internal
 */
export function createFilter(json) {
    switch (json.filterType) {
        case FilterTypes.logicalAttribute:
            return new LogicalAttributeFilter(json.filters.map((f) => createFilter(f)), json.operator);
            break;
        case FilterTypes.members:
            return new MembersFilter(create(json.attribute), json.members);
            break;
        case FilterTypes.exclude:
            return new ExcludeFilter(createFilter(json.filter), json.input && createFilter(json.input));
            break;
        case FilterTypes.measure:
            return new MeasureFilter(create(json.attribute), create(json.measure), json.operatorA, json.valueA, json.operatorB, json.valueB);
            break;
        case FilterTypes.ranking:
            return new RankingFilter(create(json.attribute), create(json.measure), json.operator, json.count);
            break;
        case FilterTypes.numeric:
            return new NumericFilter(create(json.attribute), json.operatorA, json.valueA, json.operatorB, json.valueB);
            break;
        case FilterTypes.text:
            return new TextFilter(create(json.attribute), json.operatorA, json.valueA);
            break;
        case FilterTypes.relativeDate:
            return new RelativeDateFilter(create(json.attribute), json.offset, json.count, json.operator, json.anchor);
            break;
        case FilterTypes.dateRange:
            return new DateRangeFilter(create(json.attribute), json.valueA, json.valueB);
            break;
    }
    throw new TranslatableError('errors.filter.unsupportedType', {
        filterType: json.filterType,
    });
}
//# sourceMappingURL=filters.js.map